import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Home, Users, Clock, Briefcase, Star } from 'lucide-react';

const TrustSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: Award,
      title: 'Productos Premium',
      description: 'Utilizamos productos de la más alta calidad',
    },
    {
      icon: Home,
      title: 'Servicio a domicilio',
      description: 'Vamos hasta donde estés para tu comodidad',
    },
    {
      icon: Users,
      title: 'Atención personalizada',
      description: 'Cada cliente recibe un servicio único',
    },
    {
      icon: Clock,
      title: 'Más de 3 años de experiencia',
      description: 'Profesionales con amplia trayectoria',
    },
    {
      icon: Briefcase,
      title: 'Clientes particulares y empresas',
      description: 'Servicio adaptado a tus necesidades',
    },
    {
      icon: Star,
      title: 'Resultados de alta calidad',
      description: 'Superamos las expectativas de cada cliente',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section className="bg-gradient-to-b from-[#0A1628] to-[#0F1D33] py-20 md:py-28 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#60A5FA] rounded-full filter blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#3B82F6] rounded-full filter blur-3xl" />
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto px-6 relative z-10"
      >
        {/* Section Title */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            ¿Por qué elegir <span className="text-[#60A5FA]">Lavaporcar?</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Confía en profesionales que cuidan cada detalle de tu vehículo
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="group relative bg-gradient-to-br from-[#0F1D33]/80 to-[#0A1628]/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-[#60A5FA]/50 transition-all duration-300 overflow-hidden"
              >
                {/* Gradient Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#60A5FA]/0 to-[#3B82F6]/0 group-hover:from-[#60A5FA]/5 group-hover:to-[#3B82F6]/5 transition-all duration-500" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#60A5FA]/30 group-hover:scale-110 transition-all duration-300">
                    <Icon className="w-7 h-7 text-[#60A5FA]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

export default TrustSection;